// =====================================================================
//  ADMINISTRACIÓN — usuarios, comunas y provincias
//
//  Solo máster. RLS ya lo aplica en el motor: un analista que llegue acá
//  por la dirección no puede escribir nada, y las políticas están
//  probadas. Esta vista es la comodidad, no la seguridad.
//
//  Sobre dar acceso: alcanza con escribir el correo. No hace falta que la
//  persona haya entrado nunca, ni que entre después — los dos triggers del
//  esquema enlazan la cuenta de Google en cualquiera de los dos órdenes.
// =====================================================================

import { useState } from 'react'
import {
  useActualizarComuna,
  useActualizarUsuario,
  useComunas,
  useConcesiones,
  useCrearComuna,
  useCrearUsuario,
  useBorrarUsuario,
  useUsuarios,
  useRoles,
} from '../datos/consultas'
import { Alerta, Cargando, ErrorCarga, Tag, TituloCard, useAvisar } from '../componentes/ui'
import { Pagina } from '../App'
import { fechaHora } from '../dominio/formato'
import type { RolUsuario } from '../tipos/dominio'

/**
 * Respaldo para el desplegable de roles.
 *
 * Las etiquetas y la descripción salen de la tabla rol_descripcion, pero
 * si esa consulta falla —porque la migración todavía no se aplicó, o por un
 * corte de red— el desplegable quedaría vacío y no se podría dar acceso a
 * nadie.
 * Los tres valores del enum sí son parte del contrato del esquema, así que
 * sirven de red: se puede seguir trabajando, con etiquetas más pobres.
 */
const ROLES_MINIMOS = [
  { rol: 'MASTER' as RolUsuario, etiqueta: 'Máster', resumen: '' },
  { rol: 'ANALISTA' as RolUsuario, etiqueta: 'Analista', resumen: '' },
  { rol: 'LECTOR' as RolUsuario, etiqueta: 'Lector', resumen: '' },
]

export function Admin() {
  const usuarios = useUsuarios()
  const comunas = useComunas()
  const concesiones = useConcesiones()
  const crearUsuario = useCrearUsuario()
  const actualizarUsuario = useActualizarUsuario()
  const borrarUsuario = useBorrarUsuario()
  const crearComuna = useCrearComuna()
  const actualizarComuna = useActualizarComuna()
  const rolesConsulta = useRoles()
  const avisar = useAvisar()

  // Si rol_descripcion no está disponible, el desplegable igual funciona.
  const roles = rolesConsulta.data?.length ? rolesConsulta.data : ROLES_MINIMOS

  const [correo, setCorreo] = useState('')
  const [nombre, setNombre] = useState('')
  const [rol, setRol] = useState<RolUsuario>('ANALISTA')
  const [nuevaComuna, setNuevaComuna] = useState('')
  const [provincia, setProvincia] = useState('')

  if (usuarios.isError) return <ErrorCarga error={usuarios.error} />
  if (usuarios.isLoading) return <Cargando />

  const agregar = async () => {
    const email = correo.trim().toLowerCase()
    if (!email.includes('@')) {
      avisar(new Error('Ese correo no parece válido.'))
      return
    }
    try {
      await crearUsuario.mutateAsync({ email, nombre, rol })
      setCorreo('')
      setNombre('')
    } catch (e) {
      avisar(e)
    }
  }

  /**
   * Eliminar es distinto de quitar el acceso, y la diferencia no es obvia:
   * la fila se va y con ella el «quién tocó esto» de cada ficha que esa
   * persona haya editado. La bitácora sobrevive igual, porque no tiene
   * claves ajenas.
   */
  const eliminar = async (id: number, email: string) => {
    const confirmado = window.confirm(
      `¿Eliminar a ${email}?\n\n` +
        'La fila desaparece y las fichas que haya editado dejan de mostrar ' +
        'quién las modificó por última vez. La bitácora se conserva completa.\n\n' +
        'Si solo se busca cerrarle el acceso, «Quitar acceso» hace eso sin perder nada.',
    )
    if (!confirmado) return
    try {
      await borrarUsuario.mutateAsync(id)
    } catch (e) {
      avisar(e)
    }
  }

  const agregarComuna = async () => {
    if (!nuevaComuna.trim() || !provincia.trim()) return
    try {
      await crearComuna.mutateAsync({ nombre: nuevaComuna, provincia })
      setNuevaComuna('')
      setProvincia('')
    } catch (e) {
      avisar(e)
    }
  }

  const activos = (usuarios.data ?? []).filter((u) => u.activo)
  const porComuna = (nombre: string) =>
    (concesiones.data ?? []).filter((c) => c.comuna === nombre).length

  return (
    <>
      <Pagina
        titulo="Comunas y usuarios"
        sub="Administración reservada al rol máster. Los permisos los aplica la base de datos, no esta pantalla."
      />

      {/* ---------------- Usuarios ---------------- */}
      <div className="card" style={{ marginBottom: 16 }}>
        <TituloCard>
          Quién puede entrar
          <span className="sub" style={{ textTransform: 'none', letterSpacing: 0 }}>
            {' '}
            · {activos.length} con acceso
          </span>
        </TituloCard>

        <table>
          <thead>
            <tr>
              <th>Correo</th>
              <th>Nombre</th>
              <th>Rol</th>
              <th>Estado</th>
              <th>Último acceso</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(usuarios.data ?? []).map((u) => {
              const esMaestro = u.protegido
              return (
                <tr key={u.id} className={u.activo ? undefined : 'inactiva'}>
                  <td className="mono">{u.email}</td>
                  <td className="sub">{u.nombre ?? '—'}</td>
                  <td>
                    {esMaestro ? (
                      <Tag clase="t-nuestra">Máster</Tag>
                    ) : (
                      <select
                        value={u.rol}
                        style={{
                          background: 'var(--panel2)',
                          border: '1px solid var(--line)',
                          borderRadius: 7,
                          padding: '4px 7px',
                          fontSize: 12,
                        }}
                        onChange={(e) =>
                          actualizarUsuario
                            .mutateAsync({ id: u.id, cambio: { rol: e.target.value as RolUsuario } })
                            .catch(avisar)
                        }
                      >
                        {roles.map((r) => (
                          <option key={r.rol} value={r.rol}>
                            {r.etiqueta}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    {!u.activo ? (
                      <Tag clase="t-desc">Sin acceso</Tag>
                    ) : u.auth_id ? (
                      <Tag clase="t-pagada">Activo</Tag>
                    ) : (
                      <Tag clase="t-remate">Registrado, nunca entró</Tag>
                    )}
                  </td>
                  <td className="sub">{u.ultimo_acceso ? fechaHora(u.ultimo_acceso) : '—'}</td>
                  <td>
                    {esMaestro ? (
                      <span className="sub">máster permanente</span>
                    ) : (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn sm"
                          onClick={() =>
                            actualizarUsuario
                              .mutateAsync({ id: u.id, cambio: { activo: !u.activo } })
                              .catch(avisar)
                          }
                        >
                          {u.activo ? 'Quitar acceso' : 'Devolver acceso'}
                        </button>
                        <button
                          className="btn sm"
                          title="Elimina la fila. Para quitar el acceso conservando el historial, usa «Quitar acceso»."
                          style={{ color: 'var(--bad)', borderColor: '#5c2530' }}
                          onClick={() => eliminar(u.id, u.email)}
                        >
                          Eliminar
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--line)' }}>
          <TituloCard>Dar acceso a alguien</TituloCard>

          {/* Todo en una línea: es un formulario de tres campos y un botón,
              y separarlo en filas lo hacía parecer más largo de lo que es. */}
          <div className="form-linea">
            <div className="field">
              <label>Correo de Google</label>
              <input
                type="email"
                value={correo}
                placeholder="persona@dominio.cl"
                onChange={(e) => setCorreo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void agregar()}
              />
            </div>
            <div className="field">
              <label>Nombre (opcional)</label>
              <input
                value={nombre}
                placeholder="lo toma de Google"
                onChange={(e) => setNombre(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void agregar()}
              />
            </div>
            <div className="field">
              <label>Rol</label>
              <select value={rol} onChange={(e) => setRol(e.target.value as RolUsuario)}>
                {roles.map((r) => (
                  <option key={r.rol} value={r.rol}>
                    {r.etiqueta}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="btn pri"
              disabled={crearUsuario.isPending}
              onClick={() => void agregar()}
            >
              {crearUsuario.isPending ? 'Registrando…' : 'Dar acceso'}
            </button>
          </div>

          {/* La leyenda solo aparece si hay algo que explicar. Con el
              respaldo de ROLES_MINIMOS los resúmenes vienen vacíos, y
              renderizarlos igual dejaba «Máster —» con la raya colgando. */}
          {roles.some((r) => r.resumen) ? (
            <div className="sub" style={{ marginTop: 8 }}>
              {roles
                .filter((r) => r.resumen)
                .map((r) => (
                  <div key={r.rol}>
                    <b>{r.etiqueta}</b> — {r.resumen}
                  </div>
                ))}
            </div>
          ) : null}

          <Alerta tono="info" estilo={{ marginTop: 16 }}>
            <b>Con escribir el correo alcanza.</b> La persona entra por Google cuando quiera y el
            enlace se hace solo, sin importar si ya había intentado entrar antes de quedar
            registrada. Verá el listado según el rol asignado, y nada si se le quita el acceso.
          </Alerta>
        </div>
      </div>

      {/* ---------------- Comunas ---------------- */}
      <div className="card">
        <TituloCard>Comunas y provincias</TituloCard>
        <table>
          <thead>
            <tr>
              <th>Comuna</th>
              <th>Provincia</th>
              <th>Región</th>
              <th className="num">Concesiones</th>
              <th>Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(comunas.data ?? []).map((c) => (
              <tr key={c.id}>
                <td className="nm">{c.nombre}</td>
                <td className="sub">{c.provincia}</td>
                <td className="sub">{c.region}</td>
                <td className="num">{porComuna(c.nombre) || '—'}</td>
                <td>
                  <Tag clase={porComuna(c.nombre) > 0 ? 't-pagada' : 't-desc'}>
                    {porComuna(c.nombre) > 0 ? 'Con nómina cargada' : 'Sin nómina'}
                  </Tag>
                </td>
                <td>
                  {porComuna(c.nombre) === 0 ? (
                    <button
                      className="btn sm"
                      onClick={() =>
                        actualizarComuna.mutateAsync({ id: c.id, activa: !c.activa }).catch(avisar)
                      }
                    >
                      {c.activa ? 'Marcar inactiva' : 'Marcar activa'}
                    </button>
                  ) : (
                    <span className="sub">tiene datos</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="form-linea" style={{ marginTop: 14 }}>
          <div className="field">
            <label>Comuna nueva</label>
            <input
              value={nuevaComuna}
              placeholder="POZO ALMONTE"
              onChange={(e) => setNuevaComuna(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Provincia</label>
            <input
              value={provincia}
              placeholder="Tamarugal"
              onChange={(e) => setProvincia(e.target.value)}
            />
          </div>
          <button className="btn pri" disabled={crearComuna.isPending} onClick={() => void agregarComuna()}>
            Agregar comuna
          </button>
        </div>

        <Alerta tono="warn" estilo={{ marginTop: 16 }}>
          Agregar una comuna <b>no</b> carga sus concesiones. Cada comuna necesita su nómina de la
          Tesorería, y si es de otra provincia probablemente sea <b>otra causa ante otro tribunal</b>,
          con su propia fecha de remate. Pozo Almonte pertenece a la provincia del Tamarugal y está
          creada e inactiva justamente por eso.
        </Alerta>
      </div>
    </>
  )
}
